import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  Dialog,
  IconButton,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import { motion, AnimatePresence } from 'framer-motion';
import { portfolioData, portfolioCategories } from '../data/portfolioData';

export default function PortfolioGallery() {
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [openLightbox, setOpenLightbox] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredData = portfolioData.filter(item => 
    activeFilter === 'Todos' ? true : item.category === activeFilter
  );

  const handleOpenProject = (project) => {
    setSelectedProject(project);
    setOpenLightbox(true);
  };

  const handleCloseProject = () => {
    setOpenLightbox(false);
    setTimeout(() => setSelectedProject(null), 300); // Wait for transition
  };

  return (
    <Box sx={{ mb: 10 }}>
      <Typography variant="h3" sx={{ fontWeight: 800, mb: 4, textAlign: 'center' }}>
        Nuestro <span style={{ color: '#1976d2' }}>Portafolio</span>
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 6 }}>
        {portfolioCategories.map(category => (
          <Button
            key={category}
            variant={activeFilter === category ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setActiveFilter(category)}
            sx={{ 
              borderRadius: 8, 
              textTransform: 'none', 
              fontWeight: 600,
              px: 3
            }}
          >
            {category}
          </Button>
        ))}
      </Box>

      {/* Grid Gallery */}
      <Grid container spacing={4}>
        <AnimatePresence mode="popLayout">
          {filteredData.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id} component={motion.div}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <Card 
                onClick={() => handleOpenProject(item)}
                elevation={0}
                sx={{ 
                  height: '100%', 
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #eaeaea', 
                  borderRadius: 4,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  transition: 'all 0.3s', 
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 15px 35px rgba(0,0,0,0.1)' } 
                }}
              >
                <Box sx={{ 
                  height: 220, 
                  overflow: 'hidden', 
                  backgroundColor: '#f5f5f5',
                  position: 'relative',
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'top',
                    transition: 'transform 0.5s ease'
                  },
                  '&:hover img': {
                    transform: 'scale(1.05)'
                  }
                }}>
                  <img src={item.image} alt={item.title} loading="lazy" />
                  <Chip 
                    label={item.category} 
                    size="small"
                    color="primary"
                    sx={{ position: 'absolute', top: 16, right: 16, fontWeight: 700, boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}
                  />
                </Box>
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{item.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 3 }}>
                    {item.description}
                  </Typography>
                  <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="button" color="primary" sx={{ fontWeight: 600, textTransform: 'none' }}>Ver detalles</Typography>
                    <IconButton 
                      component="a" 
                      href={item.link} 
                      target="_blank" 
                      onClick={(e) => e.stopPropagation()} 
                      color="primary"
                      size="small"
                      title="Visitar sitio"
                      sx={{ backgroundColor: 'rgba(25,118,210,0.1)', '&:hover': { backgroundColor: 'rgba(25,118,210,0.2)' } }}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </AnimatePresence>
      </Grid>

      {/* Enriched Lightbox Modal */}
      <Dialog
        open={openLightbox}
        onClose={handleCloseProject}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4, 
            overflowY: 'auto',
            overflowX: 'hidden',
            height: { xs: 'auto', md: '650px' },
            maxHeight: '90vh'
          }
        }}
      >
        {selectedProject && (
          <Grid container sx={{ height: '100%' }}>
            {/* Image Side */}
            <Grid item xs={12} md={7} sx={{ backgroundColor: '#eaeaea', position: 'relative', height: { xs: '220px', md: '100%' } }}>
              <IconButton 
                onClick={handleCloseProject} 
                sx={{ position: 'absolute', top: 16, left: 16, zIndex: 10, color: '#fff', backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' } }}
              >
                <CloseIcon />
              </IconButton>
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 6 } }}>
                <img 
                  src={selectedProject.image} 
                  alt={selectedProject.title} 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%', 
                    objectFit: 'contain', 
                    borderRadius: '8px',
                    filter: 'drop-shadow(0px 20px 30px rgba(0,0,0,0.25))'
                  }} 
                />
              </Box>
            </Grid>

            {/* Content Side */}
            <Grid item xs={12} md={5} sx={{ p: { xs: 3, md: 6 }, display: 'flex', flexDirection: 'column', height: { xs: 'auto', md: '100%' }, overflowY: 'auto' }}>
              <Chip label={selectedProject.category} color="primary" variant="outlined" sx={{ alignSelf: 'flex-start', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
                {selectedProject.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }}>
                {selectedProject.description}
              </Typography>
              
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                Tecnologías utilizadas:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 5 }}>
                {selectedProject.technologies.map(tech => (
                  <Chip key={tech} label={tech} size="small" sx={{ fontWeight: 500 }} />
                ))}
              </Box>

              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                endIcon={<LaunchIcon />}
                href={selectedProject.link}
                target="_blank"
                sx={{ borderRadius: 2, textTransform: 'none', py: 1.5, mt: 'auto' }}
              >
                Visitar Sitio Web
              </Button>
            </Grid>
          </Grid>
        )}
      </Dialog>
    </Box>
  );
}
